import { useEffect, useRef, useState } from 'react';

// material-ui
import AppBar from '@mui/material/AppBar';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Search from './Search';
import Profile from './Profile';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
import MoreOutlined from '@ant-design/icons/MoreOutlined';

// admin
import UserImpersonationSelect from 'components/admin/UserImpersonationSelect';

// ==============================|| HEADER CONTENT - MOBILE ||============================== //

export default function MobileSection() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popperRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // keep focus on the menu button when closing
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false && anchorRef.current) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // Custom click-away that ignores MUI popover/modal portals
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      const target = event.target;

      // If we somehow don't have refs yet, do nothing
      if (!anchorRef.current || !popperRef.current) return;

      // Click on toggle button or inside the popper => ignore
      if (
        anchorRef.current.contains(target) ||
        popperRef.current.contains(target)
      ) {
        return;
      }

      // Click inside any MUI Popover/Modal (e.g. Select menu, Autocomplete, Dialog) => ignore
      if (
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiModal-root')
      ) {
        return;
      }

      // Otherwise, it is a real "click away"
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          sx={(theme) => ({
            color: 'text.primary',
            bgcolor: open ? 'grey.300' : 'grey.100',
            ...theme.applyStyles('dark', {
              bgcolor: open ? 'grey.200' : 'background.default'
            })
          })}
          aria-label="open more menu"
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          color="secondary"
          variant="light"
        >
          <MoreOutlined />
        </IconButton>
      </Box>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ width: '100%' }}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 9] }
            }
          ]
        }}
        ref={popperRef}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows.z1,
                width: '100%'
              })}
            >
              <AppBar color="inherit" position="static" elevation={0}>
                <Toolbar
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 1,
                    gap: 1.25
                  }}
                >
                  {/* Search: full width */}
                  <Box sx={{ width: '100%' }}>
                    <Search />
                  </Box>

                  {/* Impersonation select: full width, stacked nicely on mobile */}
                  <Box sx={{ width: '100%' }}>
                    <UserImpersonationSelect />
                  </Box>

                  {/* Profile */}
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      mt: 0.5
                    }}
                  >
                    <Profile />
                  </Box>
                </Toolbar>
              </AppBar>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
}
